import * as Phaser from "phaser-ce"
import { CCUtils } from "../../../common/CCUtils"
import { COLOR } from "../../../common/Color"
import { log } from "../../../common/Logging"
import { FontType } from "../../../common/TextUtils"
import { StudentStoreSA } from "../../../react/students/StudentStoreSA"
import { TransitionState } from "../../GameState"
import { BackgroundSprite } from "../../generic-sprites/BackgroundSprite"
import { ButtonContainer, Buttons, ButtonType } from "../../generic-sprites/Buttons"
import { loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { RadioGroup, RadioOption } from "../../generic-sprites/RadioButtons"
import { Line } from "../../generic-sprites/Shape"
import { Audio } from "../../utils/Audio"
import { ColorUtils } from "../../utils/ColorUtils"
import { PhaserText } from "../../utils/PhaserText"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale, x, y } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { AddictionRoadAtlases, AddictionRoadAudio, AddictionRoadImages, loadAssets } from "./AddictionRoadAssets"
import { AddictionRoadPos } from "./AddictionRoadPos"
import { AddictionRoadStore } from "./AddictionRoadStore"


enum ActivityStates {
    START = "START",
    INTRO = "INTRO",
    INSTRUCTION_ROUND = "INSTRUCTION_ROUND",
    DEMO_ROUND = "DEMO_ROUND",
    PRACTICE_ROUND = "PRACTICE_ROUND",
    RACE_ROUND = "RACE_ROUND",
}

enum Mode {
    TITLE = "TITLE",
    INSTRUCTIONS = "INSTRUCTIONS",
    DEMO = "DEMO",
    PRACTICE = "PRACTICE",
    RACE = "RACE"
}

enum UIMode {
    DEMO,
    PRACTICE,
    RACE
}

enum CigType {
    LEFT,
    MIDDLE,
    RIGHT
}

class Cig extends Phaser.Sprite {
    type: CigType
}

export class AddictionRoadState extends Phaser.State {

    activityState: ActivityStates

    username: string
    currentMode: Mode
    ui: Phaser.Sprite
    arrowButtons: Phaser.Sprite
    showScoresButton: ButtonContainer

    myScore: number | undefined

    car: Phaser.Sprite
    carCanMove = true
    carTurningSpeed = 6 * x(1) / 1200
    crashedCar: Phaser.Sprite
    cigs: Array<Cig> = []
    milePost: Phaser.Sprite
    milePostIsFinishLine = false
    sign: Phaser.Sprite
    signText: Phaser.Text

    timer: Phaser.Timer
    fps: number = AddictionRoadPos.baseFps()

    usernameText: Phaser.Text
    cigsHit = 0
    cigsHitText: Phaser.Text
    cigsHitBeforeCrashing = 3
    cigSizeStart = .2
    cigYVelocityStart = 200 * y(1) / 900
    cigYVelocityEnd = 600 * y(1) / 900
    leftCigXVelocityStart = -100 * x(1) / 1200
    leftCigXVelocityEnd = -300 * x(1) / 1200
    middleCigXVelocityStart = 0
    middleCigXVelocityEnd = -80 * x(1) / 1200
    rightCigXVelocityStart = 90 * x(1) / 1200
    rightCigXVelocityEnd = 150 * x(1) / 1200
    cigFlightDelay = 1000
    milePostYVelocityStart = 50 * y(1) / 900
    milePostYVelocityEnd = 170 * y(1) / 900
    cigsPerMilePost = 10

    introGroup: Phaser.Group
    instructionsGroup: Phaser.Group
    demoGroup: Phaser.Group
    practiceGroup: Phaser.Group
    raceGroup: Phaser.Group

    carGroup: Phaser.Group
    milepostGroup: Phaser.Group
    cigsGroup: Phaser.Group
    scoresGroup: Phaser.Group

    racingEngine: Phaser.Sound
    damageNoise: Phaser.Sound
    destroyNoise: Phaser.Sound

    stopForMilePost = true

    async preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setBackgroundColor(this.game, COLOR.addictionRoadGreen)

        this.game.physics.startSystem(Phaser.Physics.ARCADE)
        this.game.add.existing(new BackgroundSprite(this.game, AddictionRoadImages.ROAD))

        this.introGroup = this.game.add.group()
        this.instructionsGroup = this.game.add.group()
        this.demoGroup = this.game.add.group()
        this.practiceGroup = this.game.add.group()
        this.raceGroup = this.game.add.group()

        this.username = StudentStoreSA.instance.findUsername()
        if (!this.username) {
            this.username = "Speedy"
        }

        this.racingEngine = this.game.add.audio(AddictionRoadAudio.RACING_ENGINE)
        this.damageNoise = this.game.add.audio(AddictionRoadAudio.CAR_CRASH)
        this.destroyNoise = this.game.add.audio(AddictionRoadAudio.EXPLOSION)

        this.activityState = ActivityStates.START
        // this.activityState = ActivityStates.INTRO
        // this.activityState = ActivityStates.INSTRUCTION_ROUND
        // this.activityState = ActivityStates.DEMO_ROUND
        // this.activityState = ActivityStates.PRACTICE_ROUND
        // this.activityState = ActivityStates.RACE_ROUND
        this.progressToNextPart()
    }

    // Activity progression
    progressToNextPart = () => {
        log.debug("Progress to next part! Current part " + this.activityState)
        this.game.input.enabled = true
        switch (this.activityState) {
            case ActivityStates.START:
                this.startIntro()
                this.activityState = ActivityStates.INTRO
                break
            case ActivityStates.INTRO:
                this.startInstructionRound()
                this.activityState = ActivityStates.INSTRUCTION_ROUND
                break
            case ActivityStates.INSTRUCTION_ROUND:
                this.startDemoRound()
                this.activityState = ActivityStates.DEMO_ROUND
                break
            case ActivityStates.DEMO_ROUND:
                this.startPracticeRound()
                this.activityState = ActivityStates.PRACTICE_ROUND
                break
            case ActivityStates.PRACTICE_ROUND:
                this.startRaceRound()
                this.activityState = ActivityStates.RACE_ROUND
                break
            case ActivityStates.RACE_ROUND:
                this.game.state.start(TransitionState.ADDICTION_ROAD_EXIT)
                break
            default:
                throw new Error(`Unknown ActivityState: ${this.activityState}`)
        }
    }

    update() {
        // To accommodate monitors at fps other than 60Hz, we adjust velocities based on the framerate
        this.getFps().then(fps => this.fps = fps)

        if ([Mode.PRACTICE, Mode.RACE].indexOf(this.currentMode) != -1 && this.carCanMove) {
            const pressingLeftKeyboardArrow = this.game.input.keyboard.isDown(Phaser.Keyboard.LEFT)
            const clickingOnLeftThirdOfScreen = this.game.input.activePointer.isDown && this.game.input.activePointer.x < this.game.width / 3
            const pressingRightKeyboardArrow = this.game.input.keyboard.isDown(Phaser.Keyboard.RIGHT)
            const clickingOnRightThirdOfScreen = this.game.input.activePointer.isDown && this.game.input.activePointer.x > this.game.width * 3 / 4

            if (pressingLeftKeyboardArrow || clickingOnLeftThirdOfScreen) {
                this.moveCarLeft()
                if (this.arrowButtons && this.arrowButtons.alive) {
                    this.arrowButtons.frame = 1
                }
            } else if (pressingRightKeyboardArrow || clickingOnRightThirdOfScreen) {
                this.moveCarRight()
                if (this.arrowButtons && this.arrowButtons.alive) {
                    this.arrowButtons.frame = 2
                }
            } else {
                if (this.arrowButtons && this.arrowButtons.alive) {
                    this.arrowButtons.frame = 0
                }
            }
        }

        if (this.car && this.car.alive) {
            if (this.cigs && this.cigsGroup) {
                this.cigs.forEach(cig => {
                    // Destroy cigs after they leave the screen
                    if (cig.y > y(1)) {
                        this.cigs.splice(0, 1)
                        cig.destroy()
                    } else {
                        // Cigs grow as they approach
                        const closeness = cig.y / y(1)
                        Scale.scaleSprite(cig, this.cigSizeStart + (closeness * (1 - this.cigSizeStart)))
                        // Cigs get faster as they approach to account for them appearing to slow down as they grow
                        if (cig.alive) {
                            cig.body.velocity.y = this.cigYVelocityStart + ((this.cigYVelocityEnd - this.cigYVelocityStart) * closeness)
                            if (cig.type == CigType.LEFT) {
                                cig.body.velocity.x = this.leftCigXVelocityStart + ((this.leftCigXVelocityEnd - this.leftCigXVelocityStart) * closeness)
                            } else if (cig.type == CigType.RIGHT) {
                                cig.body.velocity.x = this.rightCigXVelocityStart + ((this.rightCigXVelocityEnd - this.rightCigXVelocityStart) * closeness)
                            } else {
                                cig.body.velocity.x = this.middleCigXVelocityStart + ((this.middleCigXVelocityEnd - this.middleCigXVelocityStart) * closeness)
                            }
                            cig.body.velocity.y *= this.fpsAdjustment()
                            cig.body.velocity.x *= this.fpsAdjustment()
                        }
                        // cig.body.velocity.y *= this.game.time.physicsElapsedMS

                        // Cigs and car collide
                        this.game.physics.arcade.collide(this.car, cig, null, () => this.collisionHandler(cig), this)
                    }
                })
            }
            if (this.milePost && this.milePost.alive) {
                const endYPosition = y(.178)
                const startYPosition = y(-.14)
                const distanceToTravel = endYPosition - startYPosition
                // When you cross the finish line, end the race
                if (this.milePostIsFinishLine && this.milePost.y >= endYPosition) {
                    this.racingEngine.stop()
                    this.game.input.enabled = false
                    this.carCanMove = false
                    if (this.arrowButtons) {
                        this.arrowButtons.frame = 0
                    }
                    this.milePost.body.velocity.y = 0
                    if (!this.sign) {
                        this.showSign("FINISH")
                    }
                    if (this.stopForMilePost) {
                        log.info("Start timer for passing mile post")
                        // Prevent this being called a ton of extra times with potentially bad effects
                        this.stopForMilePost = false
                        this.timer.add(2000, () => {
                            this.timer.stop()
                            this.progressToNextPart()
                            this.stopForMilePost = true
                        }, this)
                    }
                } else {
                    // Mileposts grow as they approach
                    const closeness = (this.milePost.y - startYPosition) / distanceToTravel  // How far along the mile post is
                    Scale.scaleSprite(this.milePost, closeness)
                    Sprites.centerInWorldXOnly(this.milePost)
                    // Mileposts get faster as they approach to account for them appearing to slow down as they grow
                    this.milePost.body.velocity.y = this.milePostYVelocityStart + ((this.milePostYVelocityEnd - this.milePostYVelocityStart) * closeness)
                    this.milePost.body.velocity.y *= this.fpsAdjustment()
                }
            }
        }
    }

    moveCarLeft = (autoSteering = false) => {
        if (this.car.x >= AddictionRoadPos.carXMin()) {
            if (autoSteering) {
                this.car.x -= this.carTurningSpeed
            } else {
                this.car.x -= this.carTurningSpeed * this.fpsAdjustment()
            }
        }
    }

    moveCarRight = (autoSteering = false) => {
        if (this.car.x <= AddictionRoadPos.carXMax()) {
            if (autoSteering) {
                this.car.x += this.carTurningSpeed
            } else {
                this.car.x += this.carTurningSpeed * this.fpsAdjustment()
            }
        }
    }

    collisionHandler = (cig: Phaser.Sprite) => {
        if (this.currentMode != Mode.INSTRUCTIONS) {
            this.cigsHit += 1
            if (this.cigsHit < this.cigsHitBeforeCrashing) {
                log.info("DAMAGE: Total hit:", this.cigsHit)
                this.damageCar()
            } else {
                log.info("EXPLODE: Total hit:", this.cigsHit)
                this.destroyCar()
            }
            this.cigs.splice(0, 1)
            cig.destroy()
        }
    }

    damageCar = () => {
        this.updateCigsHitText()
        this.damageNoise.play()
        const smoke = Sprites.add(this.game, AddictionRoadImages.SMOKE, {x: this.car.x, y: this.car.y - y(.2), group: this.carGroup})
        this.timer.add(100, () => smoke.destroy())
    }

    destroyCar = () => {
        this.updateCigsHitText()
        this.timer.stop(true)
        this.removeArrowButtons()
        this.cigsGroup.destroy()
        if (this.milePost) {
            this.milePost.destroy()
        }

        const carX = this.car.x
        const carY = this.car.y
        const explosion = Sprites.add(this.game, AddictionRoadAtlases.EXPLOSION, {x: this.car.x - x(.12), y: this.car.y - y(.25)})
        this.racingEngine.stop()
        this.destroyNoise.play()
        explosion.animations.add("run")
        explosion.animations.play("run", 10, false)
        explosion.animations.currentAnim.onComplete.add(() => {
            this.car.destroy()
            explosion.destroy()
            this.crashedCar = Sprites.add(this.game, AddictionRoadImages.CAR_CRASHED, {x: carX - x(0.005), y: carY - y(0.03)})
            this.showSign("GAME OVER")
        })
        if (this.currentMode == Mode.DEMO) {
            log.info("Car destroyed, mode demo so start audio for after demo round in 1 sec.")
            this.timer.stop()
            this.timer.add(1000, () => {
                log.info("Starting demo mode over audio.")
                Audio.add(this.game, AddictionRoadAudio.PRACTICE_ROUND, {
                    onStop: () => {
                        log.info("Stop for demo mode audio.")
                        this.makeProgressionButton()
                    }
                })
            })
        } else if (this.currentMode == Mode.PRACTICE) {
            this.timer.stop()
            this.timer.add(3000, () => {
                this.progressToNextPart()
            }, this)
        } else if (this.currentMode == Mode.RACE) {
            AddictionRoadStore.saveUserScore(this.cigsHit)
            this.myScore = this.cigsHit
            const guess = AddictionRoadStore.getAddictedGuess
            const guessAudio = guess ? AddictionRoadAudio.GUESS_CORRECT : AddictionRoadAudio.GUESS_INCORRECT
            let guessText
            if (guess) {
                guessText =
                    "You thought it would be hard to avoid addiction, and you were right! You really don’t have much control over whether or not you smoke or vape. You also don’t know how long it takes before you get addicted. The only way to avoid addiction is never to use tobacco!"
            } else {
                guessText =
                    "You were wrong! You thought you could avoid addiction, but you really don’t have much control over whether or not you smoke or vape. You also don’t know how long it takes before you get addicted. The only way to avoid addiction is never to use tobacco!"
            }
            this.timer.stop()
            this.timer.add(3000, () => {
                this.signText.destroy()
                this.signText = PhaserText.create(this.game, guessText, {x: x(0.18), y: y(0.12), wordWrapWidth: .65, fontPx: 18})
                Audio.add(this.game, guessAudio, {onStop: this.raceOutro})
            })
        }
        this.timer.start()
    }

    raceOutro = () => {
        this.signText.destroy()
        this.signText = PhaserText.create(this.game,
            "Addiction road showed you that once you start vaping or smoking, it’s easy to get addicted. Once nicotine takes over, it controls your life. The best way for you to stay in control is never to try vaping or smoking!",
            {x: x(0.18), y: y(0.12), wordWrapWidth: .65, fontPx: 18}
        )
        const outroAudio = this.game.add.audio(AddictionRoadAudio.OUTRO)
        outroAudio.onStop.addOnce(() => {
            const progressButton = this.makeProgressionButton()
            this.showScoresButton = Buttons.create(
                this.game,
                "View Scores",
                () => {
                    this.displayScores()
                    progressButton.destroy()
                },
                {y: y(.47), centerInWorldX: true, width: 0.15, enabled: true, buttonType: ButtonType.PRIMARY}
            )
        })
        outroAudio.play()
    }

    makeProgressionButton = (center?: boolean) => {
        const button = Buttons.create(
            this.game,
            "Next",
            () => {
                button.destroy()
                this.progressToNextPart()
            },
            {x: x(0.8), y: y(center ? 0.8 : 0.9), centerInWorldX: center, width: 0.15, buttonType: ButtonType.PRIMARY}
        )
        return button
    }

    startIntro = () => {
        const title = Sprites.add(this.game, AddictionRoadImages.TITLE, {y: y(.05), group: this.introGroup})
        Sprites.centerInWorldXOnly(title)
        this.startGame(Mode.TITLE)
        Audio.add(this.game, AddictionRoadAudio.INTRO, {
            onStop: () => {
                log.info("start intro over make progress button")
                this.makeProgressionButton()
            }
        })

    }

    startInstructionRound = () => {
        this.introGroup.destroy()
        if (!this.car) {
            this.addCar()
        }
        Audio.add(this.game, AddictionRoadAudio.HOW_TO_MOVE, {
            onStop: () => this.startGame(Mode.INSTRUCTIONS)
        })
    }

    startDemoRound = () => {

        this.resetGame()
        this.addCar()
        Audio.add(this.game, AddictionRoadAudio.WATCH_DEMO, {
            onStop: () => {
                const button = Buttons.create(
                    this.game,
                    "Demo",
                    () => {
                        button.destroy()
                        this.startGame(Mode.DEMO)
                    },
                    {y: y(.47), centerInWorldX: true, width: 0.15, enabled: true, buttonType: ButtonType.PRIMARY}
                )
                return button
            }
        })
    }

    startPracticeRound = () => {
        this.demoGroup.destroy()
        this.resetGame()

        // Show starting lights
        this.addCar()
        this.showSign("PRACTICE")
        this.raceWithStartingLights(Mode.PRACTICE)
    }

    startRaceRound = () => {
        this.practiceGroup.destroy()

        // Show question
        const questionTimer = this.game.time.create(true)
        this.resetGame()
        this.addCar()
        let radioGroup
        const showQuestion = () => {
            this.showSign()
            this.signText = PhaserText.create(this.game,
                "Do you think you’ll get addicted like Player X or do you think you will be able to avoid addiction and get to the finish line?",
                {x: x(0.18), y: y(0.12), wordWrapWidth: .65, fontPx: 16}
            )
            const options: Array<RadioOption> = [
                {text: "I will get addicted."},
                {text: "I will not get addicted."}
            ]
            let playButton
            radioGroup = new RadioGroup(this.game, Scale.x(0.3), Scale.y(0.2), options,
                () => {
                    const guessWillGetAddicted = radioGroup.currentlySelectedIndex == 0
                    AddictionRoadStore.instance.setAddictedGuess(guessWillGetAddicted)
                    if (!playButton) {
                        playButton = Buttons.create(
                            this.game,
                            "Play",
                            () => {
                                playButton.destroy()
                                this.signText.destroy()
                                radioGroup.destroy()
                                this.sign.destroy()
                                Audio.add(this.game, AddictionRoadAudio.WHEN_RACE_OVER, {
                                    onStop: () => {
                                        this.showSign("RACE")
                                        this.raceWithStartingLights(Mode.RACE)
                                    }
                                })
                            },
                            {y: y(.47), centerInWorldX: true, width: 0.15, enabled: true, group: this.raceGroup, buttonType: ButtonType.PRIMARY}
                        )
                    }
                }, false
            )
        }
        questionTimer.add(0, () => Audio.add(this.game, AddictionRoadAudio.REAL_ROUND, {onStop: () => radioGroup.enable()}))
        questionTimer.add(0, showQuestion)
        // questionTimer.add(5000, showQuestion)
        questionTimer.start()
    }

    raceWithStartingLights = (mode: Mode) => {
        const readySetGo = Sprites.add(this.game, AddictionRoadAtlases.READY_SET_GO, {y: y(.148), alpha: 0, debug: true})
        Sprites.centerInWorldXOnly(readySetGo)

        const raceTimer = this.game.time.create(true)
        raceTimer.add(0, () => {
            log.info("START ENGINE")
            Audio.add(this.game, AddictionRoadAudio.REV_AND_BEEPS, {volume: 0.2})
        })
        raceTimer.add(5000, () => {
            this.signText.destroy()
            log.info("READY")
            readySetGo.alpha = 1
        })
        raceTimer.add(6000, () => {
            log.info("SET")
            readySetGo.frame = 1
        })
        raceTimer.add(7000, () => {
            log.info("GO")
            readySetGo.frame = 2
        })
        raceTimer.add(8000, () => {
            log.info("START RACE")
            readySetGo.destroy()
            this.sign.destroy()
            this.startGame(mode)
            this.racingEngine.loop = true
            this.racingEngine.play()
        })
        raceTimer.start()
    }

    resetGame = () => {
        if (this.car) {
            this.car.destroy()
        }
        if (this.crashedCar) {
            this.crashedCar.destroy()
        }
        this.cigs.forEach(cig => {
            // Destroy any remaining cigs
            this.cigs.splice(0, 1)
            cig.destroy()
        })
        this.cigsHit = 0
        if (this.sign) {
            this.sign.destroy()
        }
        if (this.cigsHitText) {
            this.cigsHitText.destroy()
        }
        if (this.usernameText) {
            this.usernameText.destroy()
        }
        if (this.signText) {
            this.signText.destroy()
        }
        if (this.ui) {
            this.ui.destroy()
        }
        if (this.milePost) {
            this.milePost.destroy()
        }
        this.milePostIsFinishLine = false
    }

    /**
     * Called every time when a new game starts.
     */
    startGame = (mode: Mode) => {

        this.milepostGroup = this.game.add.group()
        this.cigsGroup = this.game.add.group()
        this.carGroup = this.game.add.group()

        this.currentMode = mode

        this.resetGame()

        this.ui = Sprites.add(this.game, AddictionRoadAtlases.UI, {y: y(0.03)})
        this.usernameText = PhaserText.create(this.game, this.username, {x: x(0.718), y: y(0.043), debug: true})
        this.updateCigsHitText()
        this.carCanMove = true

        switch (this.currentMode) {
            case Mode.TITLE:
                this.ui.alpha = 0
                this.usernameText.alpha = 0
                this.cigsHitText.alpha = 0
                break
            case Mode.INSTRUCTIONS:
                this.ui.alpha = 0
                this.usernameText.alpha = 0
                this.cigsHitText.alpha = 0
                break
            case Mode.DEMO:
                this.ui.frame = UIMode.DEMO
                this.usernameText.alpha = 0
                break
            case Mode.PRACTICE:
                // 1 to 15 for practice
                this.cigsHitBeforeCrashing = Math.floor(Math.random() * 14) + 1
                this.ui.frame = UIMode.PRACTICE
                break
            case Mode.RACE:
                // 3 to 10 for race
                this.cigsHitBeforeCrashing = Math.floor(Math.random() * 7) + 3
                this.ui.frame = UIMode.RACE
                break
            default:
                throw new Error(`Unknown currentMode: ${this.currentMode}`)
        }

        this.addCar()
        if (this.currentMode != Mode.TITLE) {
            // Title screen just shows the running car. For everything else, set up the controls and other game elements
            this.game.physics.arcade.enable(this.car)
            this.car.body.immovable = true
            // this.game.debug.spriteBounds(this.car)

            // Cigarettes timer
            this.timer = this.game.time.create(false)
            if ([Mode.INSTRUCTIONS, Mode.DEMO].indexOf(this.currentMode) != -1) {
                // For the computer-controlled races, script the five cigarettes
                let scriptedTrajectories = [
                    [x(.49), CigType.MIDDLE],
                    [x(.47), CigType.LEFT],
                    [x(.49), CigType.MIDDLE],
                    [x(.52), CigType.RIGHT],
                    [x(.49), CigType.MIDDLE],
                ]
                const demoScriptedTrajectories = [
                    [x(.49), CigType.MIDDLE],
                    [x(.52), CigType.RIGHT],
                    [x(.49), CigType.MIDDLE],
                    [x(.52), CigType.RIGHT],
                    [x(.47), CigType.LEFT],
                ]

                const turn = (direction: "left" | "right", startAt: number, goFor: number) => {
                    for (let i = 0; i < goFor; i += 10) {
                        this.timer.add(startAt + i, () => {
                            if (direction == "left") {
                                this.moveCarLeft(true)
                            } else {
                                this.moveCarRight(true)
                            }
                        })
                    }
                }

                const goLeft = (startAt: number, goFor: number) => turn("left", startAt, goFor)

                const goRight = (startAt: number, goFor: number) => turn("right", startAt, goFor)

                if (Mode.INSTRUCTIONS == this.currentMode) {
                    // Arrow key presses
                    this.addArrowButtons()
                    this.timer.add(2500, () => this.arrowButtons.frame = 1)
                    this.timer.add(2900, () => this.arrowButtons.frame = 0)
                    this.timer.add(3500, () => this.arrowButtons.frame = 2)
                    this.timer.add(3900, () => this.arrowButtons.frame = 0)
                    this.timer.add(4500, () => this.arrowButtons.frame = 2)
                    this.timer.add(5000, () => this.arrowButtons.frame = 0)
                    this.timer.add(5500, () => this.arrowButtons.frame = 1)
                    this.timer.add(6000, () => this.arrowButtons.frame = 0)
                    this.timer.add(6500, () => this.arrowButtons.frame = 1)
                    this.timer.add(7000, () => this.arrowButtons.frame = 0)
                    this.timer.add(7500, () => this.arrowButtons.destroy())

                    // Instructions car movements
                    goLeft(2500, 400)
                    goRight(3500, 400)
                    goRight(4500, 500)
                    goLeft(5500, 500)
                    goLeft(6500, 500)
                    goRight(7500, 500)
                } else {
                    // Demo cig trajectories
                    scriptedTrajectories = demoScriptedTrajectories

                    // Demo car movements
                    goRight(2500, 450)
                    goLeft(3800, 450)
                    goRight(4800, 900)
                    goLeft(5500, 900)
                }
                const makeNextCig = (index) => {
                    if (index < scriptedTrajectories.length) {
                        this.timer.add(this.cigFlightDelay, () => {
                            this.makeCig(scriptedTrajectories[index])
                            makeNextCig(index + 1)
                        }, this)
                    } else {
                        // Deploy the finish line
                        this.timer.add(this.cigFlightDelay, () => {
                            this.milePostIsFinishLine = true
                            this.makeMilePost(9)
                        }, this)
                    }
                }
                makeNextCig(0)
            } else {
                // For the player-controlled races, make it random
                this.addArrowButtons()
                let shouldMakeMoreCigs = true
                const makeNextCigPlayerRace = (cigFlightDelay: number) => {
                    if (shouldMakeMoreCigs) {
                        this.timer.add(cigFlightDelay, () => {
                            this.makeCig()
                            if (this.currentMode == Mode.PRACTICE) {
                                makeNextCigPlayerRace(cigFlightDelay)
                            } else {
                                // In the real race, cigs should come more and more frequently over time such that it's infeasible to finish by mile marker 9
                                makeNextCigPlayerRace(cigFlightDelay * 0.995)
                            }
                        })
                    }
                }
                makeNextCigPlayerRace(this.cigFlightDelay)
                let milePostFrame = 0
                const makeNextMilePost = (frame) => {
                    // Can't get to the finish line in the real race, we just stop showing signs
                    if (!(this.currentMode == Mode.RACE && frame > 8)) {
                        log.info("Mile Post", frame)
                        this.makeMilePost(frame)
                    }
                    if (frame == 9) {
                        if (this.currentMode != Mode.RACE) {
                            this.milePostIsFinishLine = true
                            shouldMakeMoreCigs = false
                        }
                    } else {
                        milePostFrame += 1
                        this.timer.add(this.cigFlightDelay * this.cigsPerMilePost, () => makeNextMilePost(milePostFrame))
                    }
                }
                this.timer.add(this.cigFlightDelay * this.cigsPerMilePost, () => makeNextMilePost(milePostFrame))
            }
            this.timer.start()
        }
    }

    addArrowButtons = () => {
        this.arrowButtons = Sprites.add(this.game, AddictionRoadAtlases.ARROW_KEYS, {y: y(.8)})
        Sprites.centerInWorldXOnly(this.arrowButtons)
    }

    removeArrowButtons = () => {
        this.arrowButtons.destroy()
    }

    addCar = () => {
        this.car = Sprites.add(this.game, AddictionRoadAtlases.CAR, {x: AddictionRoadPos.carX(), y: AddictionRoadPos.carY(), group: this.carGroup})
        this.car.name = "car"
        this.car.animations.add("run")
        this.car.animations.play("run", 10, true)
    }

    updateCigsHitText = () => {
        if (this.cigsHitText) {
            this.cigsHitText.destroy()
        }
        let outputText: string = this.cigsHit.toString()
        if (CCUtils.debug) {
            outputText = `${outputText}(${this.cigsHitBeforeCrashing})`
        }
        this.cigsHitText = PhaserText.create(this.game, outputText, {x: x(.95), y: y(.165)})
    }

    makeCig = (trajectory?: Array<number>) => {
        const trajectories = [
            [x(.47), CigType.LEFT],
            [x(.49), CigType.MIDDLE],
            [x(.52), CigType.RIGHT]
        ]

        const cigImages = [AddictionRoadImages.CIG, AddictionRoadImages.ECIGARETTE]

        const nextTrajectory = trajectory || trajectories[Math.floor(Math.random() * trajectories.length)]
        const image = cigImages[Math.floor(Math.random() * cigImages.length)]
        const cig = Sprites.add(this.game, image, {x: nextTrajectory[0], group: this.cigsGroup, debug: true})
        cig.type = nextTrajectory[1]
        this.cigs.push(cig)
        cig.name = "cig"
        this.game.physics.arcade.enable(cig)
        cig.body.velocity.y = this.cigYVelocityStart * this.fpsAdjustment()
        if (cig.type == CigType.LEFT) {
            cig.body.velocity.x = this.leftCigXVelocityStart
        } else if (cig.type == CigType.RIGHT) {
            cig.body.velocity.x = this.rightCigXVelocityStart
        } else {
            cig.body.velocity.x = this.middleCigXVelocityStart
        }
        cig.body.velocity.x *= this.fpsAdjustment()
    }

    makeMilePost = (frame: number) => {
        if (this.milePost) {
            this.milePost.destroy()
        }
        this.milePost = Sprites.add(this.game, AddictionRoadAtlases.MILE_POSTS, {y: y(-.14), scale: .13, group: this.milepostGroup, debug: true})
        // this.milePost = Sprites.add(this.game, AddictionRoadAtlases.MILE_POSTS, {y: y(-.1), scale: 1, debug: true})
        Sprites.centerInWorldXOnly(this.milePost)
        this.milePost.frame = frame
        this.game.physics.arcade.enable(this.milePost)
        this.milePost.body.velocity.y = this.milePostYVelocityStart * this.fpsAdjustment()
    }

    showSign = (text?: string) => {
        this.sign = Sprites.add(this.game, AddictionRoadAtlases.SIGN)
        Sprites.centerInWorldXOnly(this.sign)
        if (text) {
            this.signText = PhaserText.create(this.game, text, {fontPx: 70, y: y(.15), color: COLOR.red, weight: "bold"})
            Sprites.centerTextInSprite(this.sign, this.signText, true, true)
        }
    }

    displayScores = () => {
        this.scoresGroup = this.game.add.group()
        this.sign = Sprites.add(this.game, AddictionRoadAtlases.SIGN, {group: this.scoresGroup})
        this.sign.frame = 1
        Sprites.centerInWorldXOnly(this.sign)

        const scores = AddictionRoadStore.getScores().slice(0, 10)
        let currentScoreY = AddictionRoadPos.scoresScreenY() + Scale.y(0.07)
        let latestRowScreenNameEntry: Phaser.Text
        const makeRow = (fontType: FontType = FontType.BODY, screenNameText = "Username", scoreText = "Score", addictedText = "Addicted?") => {
            latestRowScreenNameEntry = PhaserText.create(this.game, screenNameText, {
                x: AddictionRoadPos.usernameX(), y: currentScoreY, fontPx: 14, type: fontType, group: this.scoresGroup
            })
            PhaserText.create(this.game, scoreText, {
                x: AddictionRoadPos.scoreX(), y: currentScoreY, fontPx: 14, type: fontType, group: this.scoresGroup
            })
            PhaserText.create(this.game, addictedText, {
                x: AddictionRoadPos.addictedX(), y: currentScoreY, fontPx: 14, type: fontType, group: this.scoresGroup
            })

        }
        makeRow()
        const screenname = StudentStoreSA.instance.findUsername()
        if (screenname != null && this.myScore != null && scores.find(score => score.screenname === screenname) == null) {
            log.info("Add my score since it wasn't there")
            scores.unshift({
                screenname: screenname,
                date: new Date(),
                score: this.myScore
            })
        }

        scores.forEach((score, index) => {
            if (index < 10) {
                currentScoreY += latestRowScreenNameEntry.height * 1.7
                new Line(this.game, AddictionRoadPos.usernameX(), latestRowScreenNameEntry.y + latestRowScreenNameEntry.height + Scale.y(0.005),
                    this.sign.width * 0.83, latestRowScreenNameEntry.y + latestRowScreenNameEntry.height + Scale.y(0.005),
                    {group: this.scoresGroup}
                )
                makeRow(FontType.MONOSPACE, score.screenname, score.score.toString(), "addicted")
            }
        })

        // Move next button into view
        this.showScoresButton.destroy()
        this.world.bringToTop(this.raceGroup)
        this.makeProgressionButton(true)
    }

    getFps = (): Promise<number> => {
        return new Promise(resolve => {
            requestAnimationFrame(t1 => {
                requestAnimationFrame(t2 => resolve(1000 / (t2 - t1)))
            })
        })
    }

    fpsAdjustment = () => AddictionRoadPos.baseFps() / this.fps
}
